import { useCallback } from "react";
import type { CommentPerson, CommentThreadDetail } from "@/domain/comment";
import {
  useCommentThreadAction,
  useCreateCommentThread,
  useDeleteCommentThread,
  useReplyToCommentThread,
} from "@/application/queries/comments/mutations";
import type { CommentCommand } from "@/application/queries/comments/commentCommand";
import { fail, succeed, type WorkflowOutcome } from "../shared/outcome";

export function useCommentActions(orgId: string, actor: CommentPerson | null) {
  const create = useCreateCommentThread(orgId);
  const reply = useReplyToCommentThread(orgId, actor);
  const act = useCommentThreadAction(orgId, actor);
  const remove = useDeleteCommentThread(orgId);
  const createThread = create.mutateAsync;
  const replyTo = reply.mutateAsync;
  const actOn = act.mutateAsync;
  const removeThread = remove.mutateAsync;

  const run = useCallback(
    async (command: CommentCommand): Promise<WorkflowOutcome<CommentThreadDetail | null>> => {
      try {
        if (command.action === "create") return succeed(await createThread(command.draft));
        if (command.action === "reply") return succeed(await replyTo(command));
        if (command.action === "deleteThread") await removeThread(command.uuid);
        else await actOn(command);
        return succeed(null);
      } catch (error) {
        return fail(error);
      }
    },
    [createThread, replyTo, actOn, removeThread],
  );

  return {
    run,
    isPending: create.isPending || reply.isPending || act.isPending || remove.isPending,
  };
}
